/**
 * Unified slug-translation API.
 * ─────────────────────────────────────────────────────────────────────────
 * Three kinds of slugs live in this app and each used to have its own
 * lookup function:
 *
 *   1. "route"            — top-level URL segments (`/guides` ↔ `/de/reisefuehrer`)
 *                           Underlying data: `shared/route-slugs.ts` (SLUG_TRANS).
 *
 *   2. "guide"            — long-form editorial guide slugs that live AFTER
 *                           the `/guides/...` segment (`best-time-to-visit-croatia`
 *                           ↔ `beste-reisezeit-kroatien`).
 *                           Underlying data: `shared/guide-slug-data.ts` (GUIDE_SLUG_TRANS).
 *
 *   3. "destination-blog" — long-form blog pages keyed by destination slug.
 *                           These are NOT translated — `zadar` stays `zadar`
 *                           in every language. Underlying data:
 *                           `shared/destination-blog-slugs.ts`
 *                           (DESTINATION_BLOG_SLUGS, an existence set).
 *
 * The three underlying files are still the canonical data sources, but every
 * caller should reach for `getLocalizedSlug` / `resolveLocalizedSlug` /
 * `hasDestinationBlog` from THIS file rather than the per-kind helpers.
 * Falls-back identically across all kinds: if the slug is not found in any
 * map, the input is returned unchanged.
 */

import { NON_EN_LANG_CODES, type NonEnLang } from './languages';
import { SLUG_TRANS, SLUG_TRANS_REVERSE } from './route-slugs';
import { GUIDE_SLUG_TRANS } from './guide-slug-data';
import { DESTINATION_BLOG_SLUGS } from './destination-blog-slugs';

export type { NonEnLang } from './languages';

export type SlugKind = 'route' | 'guide' | 'destination-blog';

const FORWARD_MAPS: Record<'route' | 'guide', Record<string, Partial<Record<NonEnLang, string>>>> = {
  route: SLUG_TRANS,
  guide: GUIDE_SLUG_TRANS,
};

// Reverse maps: translated-slug → English slug, per language.
// Built once at module load from the forward maps so adding a new translation
// row never requires touching the reverse path.
const GUIDE_REVERSE: Record<NonEnLang, Record<string, string>> = Object.fromEntries(
  NON_EN_LANG_CODES.map(lang => [lang, {}])
) as Record<NonEnLang, Record<string, string>>;
for (const [eng, langs] of Object.entries(GUIDE_SLUG_TRANS)) {
  for (const [lang, translated] of Object.entries(langs)) {
    if (translated) GUIDE_REVERSE[lang as NonEnLang][translated] = eng;
  }
}

const REVERSE_MAPS: Record<'route' | 'guide', Record<NonEnLang, Record<string, string>>> = {
  route: SLUG_TRANS_REVERSE,
  guide: GUIDE_REVERSE,
};

const DESTINATION_BLOG_SET: ReadonlySet<string> = new Set<string>(DESTINATION_BLOG_SLUGS);

/**
 * English slug → localised slug for the given kind + language.
 * Returns the input unchanged if no translation exists, or if `kind` is
 * `destination-blog` (those slugs are intentionally identical across languages).
 */
export function getLocalizedSlug(kind: SlugKind, englishSlug: string, lang: NonEnLang): string {
  if (kind === 'destination-blog') return englishSlug;
  return FORWARD_MAPS[kind][englishSlug]?.[lang] ?? englishSlug;
}

/**
 * Localised slug → English slug for the given kind.
 *
 * If `lang` is provided, only that language's reverse map is consulted (faster).
 * If `lang` is omitted, every non-English map is scanned in NON_EN_LANG_CODES
 * order — useful when the URL parser doesn't yet know which language a slug
 * came from. For `destination-blog` kind the input is returned unchanged.
 */
export function resolveLocalizedSlug(kind: SlugKind, slug: string, lang?: NonEnLang): string {
  if (kind === 'destination-blog') return slug;
  if (lang) return REVERSE_MAPS[kind][lang]?.[slug] ?? slug;
  for (const l of NON_EN_LANG_CODES) {
    const eng = REVERSE_MAPS[kind][l]?.[slug];
    if (eng) return eng;
  }
  return slug;
}

/**
 * Existence check for a destination-blog slug. Re-exported from
 * `shared/destination-blog-slugs.ts` so all three slug kinds have a single
 * import surface.
 */
export function hasDestinationBlog(slug: string | undefined | null): boolean {
  return typeof slug === 'string' && DESTINATION_BLOG_SET.has(slug);
}
